import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChildren } from "@angular/core";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { BehaviorSubject, combineLatest, Observable, of, Subject } from "rxjs";
import { delay, filter, first, takeUntil } from "rxjs/operators";
import { MultiSelectChipOption } from "src/app/components/multi-select-chips/multi-select-chips.component";
import { limitEndTime } from "src/app/components/time-picker/util/limit-end-time";
import { ZonesSelectorComponent } from "src/app/components/zones-selector/zones-selector.component";
import { DisplayedCategory, DisplayedZone, InspectionType, MirRule } from "src/app/models/mir-rule.model";
import { InspectionDetail } from "src/app/models/openAPIAliases";
import { supportedTimezones } from "src/app/models/supported-timezone";
import { LanguageDictionaryService } from "src/app/services/language-dictionary/language-dictionary.service";
import { RulesService } from "src/app/services/rules/rules.service";
import { ZonesService } from "src/app/services/zones.service";
import { isDefined } from "src/utils/isDefined/isDefined";
import { WizardId, WizardService } from "../../../services/wizard/wizard.service";
import { StepProps } from "../step-props";

export enum TripSemantics {
	tripStart = "tripStart",
	tripEnd = "tripEnd",
}

export enum RadioButtonValue {
	ZONE = "ZONE",
	MOVES = "MOVES",
	POWER = "POWER",
}
export function makeKey(step: string, semantics: TripSemantics) {
	return `${step}/${semantics}`;
}
export interface SelectOption {
	label: string;
	value: string;
}

export enum TripFrequencyUi {
	trip = "trip",
	workPeriod = "work_period",
	tripWithinWorkperiod = "trip-within-workperiod",
}
@Component({
	selector: "app-define-duty",
	templateUrl: "./define-duty.component.html",
	styleUrls: ["./define-duty.component.scss"],
})
export class DefineDutyComponent implements OnInit, OnDestroy, AfterViewInit {
	constructor(
		private fb: FormBuilder,
		private wizardService: WizardService,
		private langService: LanguageDictionaryService,
		private dialog: MatDialog,
		private zonesService: ZonesService,
		private ref: ElementRef,
		private rulesService: RulesService,
	) {}
	@Input() semantics: TripSemantics;
	@Input() wantMovementOption: boolean;

	@Input() reset$: Observable<void>;

	@ViewChildren("allWeekCheckbox") allWeekCheckboxes!: QueryList<ElementRef<HTMLInputElement>>;

	form!: FormGroup;
	maxWorkPeriods = 3;
	TripFrequency = TripFrequencyUi;
	weekdays = ["Su", "M", "T", "W", "Th", "F", "S"];
	dayFormKeys = ["Su", "M", "T", "W", "Th", "F", "S"];
	addLabel = "";
	inspectionTypes: MultiSelectChipOption[] = [];
	wizardId = WizardId.createRule;
	zones: MultiSelectChipOption[] = [];
	timeZones: SelectOption[] = [];
	tripType: string;
	destroy$ = new Subject<void>();
	isLeftActive = false;
	isRightActive = false;
	zonesSelectionLabel = "Select Zones";
	disableZoneDropdown = true;
	radioButtonValues = RadioButtonValue;
	limitEndTime = limitEndTime;

	divisionId: string = null;
	hasOneDivision$ = this.zonesService.hasOneDivision$.pipe(delay(0));

	categoryMap$: BehaviorSubject<Map<string, DisplayedCategory[]>> = null;

	minuteOptions: SelectOption[] = [
		{
			label: "15 minutes",
			value: "15",
		},
		{
			label: "30 minutes",
			value: "30",
		},
		{
			label: "45 minutes",
			value: "45",
		},
		{
			label: "1 hour",
			value: "60",
		},
		{
			label: "2 hours",
			value: "120",
		},
		{
			label: "3 hours",
			value: "180",
		},
	];
	powerLabel: Record<TripSemantics, string> = {
		[TripSemantics.tripStart]: "Power on",
		[TripSemantics.tripEnd]: "Power off",
	};

	whenZoneLabel: Record<TripSemantics, string> = {
		[TripSemantics.tripStart]: "exited",
		[TripSemantics.tripEnd]: "entered",
	};

	hasZonesSelected = false;

	get inspectionTypes$(): BehaviorSubject<MultiSelectChipOption[]> {
		return this.semantics === TripSemantics.tripStart
			? this.rulesService.startInspectionTypes$
			: this.rulesService.endInspectionTypes$;
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private _filterZones(zones: DisplayedZone[]) {
		return zones.length ? zones.filter(z => z.divisionId === this.divisionId || !z.divisionId) : zones;
	}

	ngAfterViewInit(): void {
		const inspectionSelect = (this.ref.nativeElement as HTMLElement).querySelector<HTMLSelectElement>(
			"app-multi-select-chips select",
		);
		inspectionSelect?.focus();

		const zoneRadioBtn = this.ref.nativeElement.querySelector("#zone-radio-btn");

		// enable zone dropdown if user goes back to either define duty component and the radio button is checked
		if (zoneRadioBtn?.checked) {
			of(true)
				.pipe(delay(0), first())
				.subscribe(_ => {
					this.disableZoneDropdown = false;
				});
		}

		this.selectedZones$.pipe(takeUntil(this.destroy$)).subscribe(zones => {
			if (Array.isArray(zones)) {
				// only bring in zones assocoated with the selected division fro getting started page
				const filteredZones = this._filterZones(zones);

				this.setZonePlaceholderText(filteredZones);
			}
		});
	}

	getZones(zonesObs$: Observable<DisplayedZone[]>, divisionId: string) {
		if (divisionId) {
			this.divisionId = divisionId;
		}

		zonesObs$.pipe(takeUntil(this.destroy$)).subscribe(zones => {
			if (zones) {
				// only bring in zones assocoated with the selected division from getting started page
				const filteredZones = this._filterZones(zones);
				this.setZonePlaceholderText(filteredZones);
			}
		});
	}

	get selectedZones$(): BehaviorSubject<DisplayedZone[]> {
		return this.semantics === TripSemantics.tripStart
			? this.zonesService.selectedZonesStart$
			: this.zonesService.selectedZonesEnd$;
	}

	private setInspTypesForPatchedRuleFwd(inspChips: { selected: boolean; value: string[] }[], rule: MirRule) {
		const inspTypes: InspectionType[] = inspChips
			.filter(chip => chip.selected)
			.map(chip => {
				return {
					inspectionType: chip.value[0],
				};
			});

		const newRule = {
			...rule,
			[this.semantics]: {
				...rule[this.semantics],
				inspectionTypes: inspTypes,
			},
		};
		this.rulesService.ruleToPatch$.next(newRule);
	}

	setPersistedInspTypes() {
		const insp$ = this.inspectionTypes$.pipe(first());

		const rule$ = this.rulesService.ruleToPatch$.pipe(filter(isDefined), first());
		const ruleAndInsp$ = combineLatest([rule$, insp$]);

		ruleAndInsp$.subscribe(([rule, cachedInspTypes]) => {
			if (!rule?.[this.semantics]) {
				return;
			}

			let dict = null;

			if (cachedInspTypes?.length) {
				dict = cachedInspTypes.reduce((acc, insp) => {
					acc[insp.value[1]] = true;
					return acc;
				}, {});
			}
			const inspectionTypes: MultiSelectChipOption[] = this.wizardService.getValue(
				this.wizardId,
				makeKey(StepProps.DefineDuty_InspectionTypes, this.semantics),
			) as MultiSelectChipOption[];

			if (dict && Object.keys(dict).length > 0) {
				inspectionTypes.forEach(insp => {
					if (dict[insp.value[1]]) {
						insp.selected = true;
					} else {
						insp.selected = false;
					}
				});

				this.inspectionTypes = inspectionTypes;

				this.setInspTypesForPatchedRuleFwd(this.inspectionTypes, rule);
				return;
			}

			const selectedIds = new Set(
				rule[this.semantics].inspectionTypes.map(insp => insp.inspectionType),
			);

			inspectionTypes.forEach(insp => {
				insp.selected = selectedIds.has(insp.value[0]);
			});

			this.inspectionTypes = inspectionTypes;

			this.setInspTypesForPatchedRuleFwd(this.inspectionTypes, rule);
		});
	}

	ngOnInit(): void {
		this.closeCategoryZoneDialog();
		this.categoryMap$ = this.zonesService.categoryMaps[this.semantics].obs;

		this.zonesService.divisionId$.pipe(takeUntil(this.destroy$)).subscribe(divId => {
			this.divisionId = divId;
			this.getZones(this.selectedZones$, this.divisionId);
		});

		this.tripType = this.semantics === TripSemantics.tripStart ? "Start" : "End";

		// listen for reset requests
		this.reset$.pipe(takeUntil(this.destroy$)).subscribe(this.onReset.bind(this));

		// get the config -- loaded in first step of the wizard
		const config = this.wizardService.getValue(this.wizardId, StepProps.Common_Config) as any;

		// load the inspectionTypes from the config
		const inspectionDetailLangKeys: string[] = [];
		(config.inspectionDetails as InspectionDetail[]).forEach(inspectionDetail => {
			inspectionDetailLangKeys.push((inspectionDetail as any).inspectionDetailLangKey);
		});

		// populate the timezones
		this.timeZones = supportedTimezones;

		// restore any previous values
		const { restoredSomethingLeft, restoredSomethingRight } = this.restore();
		this.form.valueChanges.subscribe(formValue => {
			// if start condition == power, then clear zones
			if (formValue.startCondition) {
				this.makeLeftActive();
			}
			if (formValue.startCondition === "power") {
				this.zones = this.zones.map(zone => {
					return {
						...zone,
						selected: false,
					};
				});
				this.update();
			}

			if (formValue.timeZone) {
				this.makeRightActive();
			}
			if (
				formValue.frequency == TripFrequencyUi.tripWithinWorkperiod ||
				formValue.frequency == TripFrequencyUi.workPeriod
			) {
				this.makeRightActive();
			}
			this.update();
			this.updateNextBtnState();
		});

		// min 1 work period
		if (this.workPeriods.length === 0) {
			this.workPeriods.push(this.createWorkPeriod());
		}

		this.trackWorkPeriodChanges();

		this.updateAddlabel();

		if (restoredSomethingLeft) {
			this.makeLeftActive();
		}
		if (restoredSomethingRight) {
			this.makeRightActive();
		}
	}
	trackWorkPeriodChanges() {
		// as a convenience check "all week" if all days are selected
		this.workPeriods.controls.forEach((period: FormGroup) => {
			const daysGroup = period.get("days") as FormGroup;
			daysGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(days => {
				const allSelected = Object.values(days).every(Boolean);
				period.get("allWeek")?.setValue(allSelected, { emitEvent: false });

				// selecting any day makes the right side active
				if (Object.values(days).some(Boolean)) {
					this.makeRightActive();
				}
			});
			period
				.get("start")
				?.valueChanges.pipe(takeUntil(this.destroy$))
				.subscribe(val => {
					if (val) {
						this.makeRightActive();
					}
				});
			period
				.get("end")
				?.valueChanges.pipe(takeUntil(this.destroy$))
				.subscribe(val => {
					if (val) {
						this.makeRightActive();
					}
				});
		});
	}
	isStartTimeMissing(index: number): boolean {
		const start = this.workPeriods.at(index).get("start")?.value;
		return !start || start === "00:00 AM";
	}

	isEndTimeMissing(index: number): boolean {
		const end = this.workPeriods.at(index).get("end")?.value;
		return !end || end === "00:00 AM";
	}
	private isEmptyTime(v?: string | null): boolean {
		if (!v) return true;
		const s = v.trim().toUpperCase();
		return s === "00:00" || s === "00:00 AM" || s === "00:00 PM";
	}

	private toMinutes12(time: string): number | null {
		if (this.isEmptyTime(time)) return null;
		const [t, pRaw] = time.trim().split(" ");
		const [hStr, mStr] = (t || "").split(":");
		let h = parseInt(hStr || "", 10);
		const m = parseInt(mStr || "", 10);
		const p = (pRaw || "AM").toUpperCase();

		if (Number.isNaN(h) || Number.isNaN(m)) return null;

		// 12h → minutes since midnight
		if (p === "AM") h = h === 12 ? 0 : h;
		else h = h === 12 ? 12 : h + 12;

		return h * 60 + m;
	}
	private fromMinutesTo12(totalMin: number): string {
		// clamp into [0, 1439]
		totalMin = Math.max(0, Math.min(1439, totalMin));
		const h24 = Math.floor(totalMin / 60);
		const m = totalMin % 60;
		const period = h24 >= 12 ? "PM" : "AM";
		let h12 = h24 % 12;
		if (h12 === 0) h12 = 12;
		const hh = h12.toString().padStart(2, "0");
		const mm = m.toString().padStart(2, "0");
		return `${hh}:${mm} ${period}`;
	}
	// Accept the period FormGroup and patch the child control
	// Replace your handler with this index-based version
	updateStartTime(timeChange: string, index: number) {
		const group = this.workPeriods.at(index) as FormGroup;

		// Set start
		group.get("start")?.setValue(timeChange);
		group.get("start")?.markAsDirty();

		// Compute End = Start + 8 hours min, but not later than 11:00 PM (23:00)
		const startMin = this.toMinutes12(timeChange);
		if (startMin != null) {
			const cappedEndMin = Math.min(startMin + 8 * 60, 23 * 60); // 23:00
			const endStr = this.fromMinutesTo12(cappedEndMin);
			group.get("end")?.setValue(endStr, { emitEvent: true });
			group.get("end")?.markAsDirty();
		} else {
			// If start is empty/sentinel, clear end too
			group.get("end")?.setValue("00:00 AM", { emitEvent: true });
			group.get("end")?.markAsDirty();
		}

		this.makeRightActive();
	}
	updateEndTime(timeChange: string, index: number) {
		const group = this.workPeriods.at(index) as FormGroup;
		group.get("end")?.setValue(timeChange);
		group.get("end")?.markAsDirty();
		this.makeRightActive();
	}
	onReset() {
		// Reset all chip selections
		this.inspectionTypes = this.inspectionTypes.map(type => ({
			...type,
			selected: false,
		}));

		// ensures no inspections are shown in review summary if we are in edit mode
		this.rulesService.ruleToPatch$.pipe(filter(isDefined), first()).subscribe(rule => {
			const ruleNoInsp = {
				...rule,
				inspectionTypes: [],
			};
			this.rulesService.ruleToPatch$.next(ruleNoInsp);
		});

		// Reset the form to defaults
		this.form.reset({
			timeZone: "",
			startCondition: "",
			expireMinutes: "15",
			frequency: TripFrequencyUi.trip,
			workPeriods: [],
		});

		// Clear all existing work periods
		while (this.workPeriods.length > 0) {
			this.workPeriods.removeAt(0);
		}

		// Add a fresh work period
		this.workPeriods.push(this.createWorkPeriod());

		this.trackWorkPeriodChanges();

		// Update wizard state
		this.updateAddlabel();
		this.updateNextBtnState();
		this.update();

		this.isLeftActive = false;
		this.isRightActive = false;

		this.selectedZones$.next(null);
		this.setZonePlaceholderText([]);
		this.zonesService.zonesOnApplyClicked = {};
		this.disableZoneDropdown = true;
	}

	updateAddlabel() {
		this.addLabel = `${this.form.value.workPeriods.length} / 3`;
	}

	get numberOfSelectedInspectionTypes(): number {
		return this.inspectionTypes.filter(inspectionType => inspectionType.selected).length;
	}
	get workPeriods(): FormArray {
		return this.form.get("workPeriods") as FormArray;
	}
	get hasSelectedInspectionTypes(): boolean {
		return !!this.inspectionTypes.find(inspectionType => inspectionType.selected);
	}
	get hasStartCondition(): boolean {
		return !!this.form?.get("startCondition")?.value;
	}
	get startCondition(): boolean {
		return this.form?.get("startCondition")?.value;
	}
	get hasFrequency(): boolean {
		return !!this.form?.get("frequency")?.value;
	}
	get isWorkPeriodFrequency(): boolean {
		return this.form?.get("frequency")?.value !== TripFrequencyUi.trip;
	}
	addWorkPeriod() {
		if (this.workPeriods.length < this.maxWorkPeriods) {
			this.workPeriods.push(this.createWorkPeriod());
			this.updateAddlabel();

			setTimeout(() => {
				if (this.allWeekCheckboxes?.length) {
					this.allWeekCheckboxes.last.nativeElement.focus();
				}
			}, 0);
		}

		this.trackWorkPeriodChanges();
	}

	removeWorkPeriod(i: number) {
		this.workPeriods.removeAt(i);
		this.updateAddlabel();
	}

	createWorkPeriod(): FormGroup {
		return this.fb.group({
			allWeek: [false],
			days: this.fb.group({
				M: [false],
				T: [false],
				W: [false],
				Th: [false],
				F: [false],
				S: [false],
				Su: [false],
			}),
			start: ["00:00 AM"],
			end: ["00:00 AM"],
		});
	}

	get tripStartGroup(): FormGroup {
		return this.form.get("tripStart") as FormGroup;
	}
	makeLeftActive() {
		this.isLeftActive = true;
	}

	makeRightActive() {
		this.isRightActive = true;
		this.isLeftActive = true;
	}

	onAllWeek(event: Event, index: number) {
		this.makeRightActive();

		const input = event.target as HTMLInputElement;
		const checked = input.checked;

		const workPeriodGroup = this.workPeriods.at(index) as FormGroup;
		const daysGroup = workPeriodGroup.get("days") as FormGroup;

		Object.keys(daysGroup.controls).forEach(dayKey => {
			daysGroup.get(dayKey)?.setValue(checked);
		});
	}

	isDaySelected(workPeriodIndex: number, day: string): boolean {
		const daysGroup = (this.workPeriods.at(workPeriodIndex) as FormGroup).get("days") as FormGroup;
		return !!daysGroup.get(day)?.value;
	}
	// restore persisted values
	restore() {
		let restoredSomethingLeft = false;
		let restoredSomethingRight = false;

		const inspectionTypes = this.wizardService.getValue(
			this.wizardId,
			makeKey(StepProps.DefineDuty_InspectionTypes, this.semantics),
		) as MultiSelectChipOption[];

		if (inspectionTypes) {
			this.inspectionTypes = [...inspectionTypes];
		} else {
			this.inspectionTypes = this.wizardService.getValue(
				this.wizardId,
				StepProps.Common_InspectionTypes,
			) as MultiSelectChipOption[];
		}

		this.setPersistedInspTypes();

		if (inspectionTypes?.find(inspectionType => inspectionType.selected)) {
			restoredSomethingLeft = true;
		}

		const timeZone = this.wizardService.getValue(
			this.wizardId,
			makeKey(StepProps.DefineDuty_Timezone, this.semantics),
		);
		if (timeZone) {
			restoredSomethingRight = true;
		}

		let expireMinutes =
			this.wizardService.getValue(this.wizardId, makeKey(StepProps.DefineDuty_ExpireMinutes, this.semantics)) ||
			"";
		if (expireMinutes && expireMinutes !== "15") {
			// don't trigger for the default
			restoredSomethingLeft = true;
		} else {
			expireMinutes = "15";
		}

		const startCondition = this.wizardService.getValue(
			this.wizardId,
			makeKey(StepProps.DefineDuty_StartCondition, this.semantics),
		);
		if (startCondition) restoredSomethingLeft = true;

		const workPeriods = this.wizardService.getValue(
			this.wizardId,
			makeKey(StepProps.DefineDuty_WorkPeriods, this.semantics),
		);
		if (Array.isArray(workPeriods) && workPeriods.filter(wp => wp.selected).length > 0) {
			restoredSomethingRight = true;
		}

		const frequency = this.wizardService.getValue(
			this.wizardId,
			makeKey(StepProps.DefineDuty_Frequency, this.semantics),
		);

		this.form = this.fb.group({
			timeZone: [timeZone || "", Validators.required],
			expireMinutes: [expireMinutes],
			startCondition: startCondition || [""],
			frequency: frequency || [TripFrequencyUi.trip],
			workPeriods: this.fb.array([]),
		});

		const workPeriodsArray = this.form.get("workPeriods") as FormArray;
		if (Array.isArray(workPeriods)) {
			workPeriods.forEach(wp => {
				workPeriodsArray.push(
					this.fb.group({
						allWeek: [wp.allWeek || false],
						days: this.fb.group({
							M: [wp.days?.M || false],
							T: [wp.days?.T || false],
							W: [wp.days?.W || false],
							Th: [wp.days?.Th || false],
							F: [wp.days?.F || false],
							S: [wp.days?.S || false],
							Su: [wp.days?.Su || false],
						}),
						start: [wp.start || "00:00"],
						end: [wp.end || "00:00"],
					}),
				);
				// restored any day?
				if (this.hasAnyDaySelected(workPeriodsArray.length - 1)) {
					restoredSomethingRight = true;
				}
				// restored if start or end is not default
				const start = wp.start;
				const end = wp.end;

				if (
					(start && start !== "00:00" && start !== "00:00 AM") ||
					(end && end !== "00:00" && end !== "00:00 AM")
				) {
					restoredSomethingRight = true;
				}
			});
		}

		this.updateNextBtnState();
		this.update();
		this.updateAddlabel();

		return { restoredSomethingLeft, restoredSomethingRight };
	}
	update() {
		const formValue = this.form.value;

		this.wizardService.setValue(
			this.wizardId,
			makeKey(StepProps.DefineDuty_InspectionTypes, this.semantics),
			this.inspectionTypes,
		);
		this.wizardService.setValue(
			this.wizardId,
			makeKey(StepProps.DefineDuty_Timezone, this.semantics),
			formValue.timeZone,
		);
		this.wizardService.setValue(
			this.wizardId,
			makeKey(StepProps.DefineDuty_ExpireMinutes, this.semantics),
			formValue.expireMinutes,
		);

		this.wizardService.setValue(
			this.wizardId,
			makeKey(StepProps.DefineDuty_StartCondition, this.semantics),
			formValue.startCondition,
		);

		this.wizardService.setValue(
			this.wizardId,
			makeKey(StepProps.DefineDuty_WorkPeriods, this.semantics),
			formValue.workPeriods,
		);
		this.wizardService.setValue(
			this.wizardId,
			makeKey(StepProps.DefineDuty_Frequency, this.semantics),
			formValue.frequency,
		);

		this.updateNextBtnState();
	}
	updateNextBtnState() {
		let enabled = true;

		const timeZoneValid = this.form.get("timeZone")?.valid && this.form.get("timeZone").value.length > 0;

		if (!timeZoneValid) {
			enabled = false;
		}

		if (this.noInspectionTypeSelected) {
			enabled = false;
		}
		const valid = this.workPeriods.controls.every(period => {
			const daysGroup = period.get("days") as FormGroup;
			return Object.values(daysGroup.value).some(v => v); // At least one true
		});

		if (!valid) {
			enabled = false;
		}

		if (enabled) {
			this.wizardService.enableNextButton(this.wizardId);
		} else {
			this.wizardService.disableNextButton(this.wizardId);
		}
	}
	hasAnyDaySelected(index: number): boolean {
		const daysGroup = this.workPeriods.at(index).get("days") as FormGroup;
		return Object.values(daysGroup.value).some(Boolean);
	}

	inspectionTypeAdded(selectedInspectionType: MultiSelectChipOption) {
		this.isLeftActive = true;

		this.inspectionTypes = this.inspectionTypes.map(inspectionType => {
			if (JSON.stringify(selectedInspectionType.value) === JSON.stringify(inspectionType.value)) {
				return {
					...inspectionType,
					selected: true,
				};
			} else {
				return { ...inspectionType };
			}
		});

		this.rulesService.ruleToPatch$.pipe(filter(isDefined), first()).subscribe(rule => {
			this.setInspTypesForPatchedRuleFwd(this.inspectionTypes, rule);
		});

		this.inspectionTypes$.next(this.inspectionTypes.filter(ins => ins.selected));

		this.update();
	}

	inspectionTypeRemoved(selectedInspectionType: MultiSelectChipOption) {
		this.inspectionTypes = this.inspectionTypes.map(inspectionType => {
			if (JSON.stringify(selectedInspectionType.value) === JSON.stringify(inspectionType.value)) {
				return {
					...inspectionType,
					selected: false,
				};
			} else {
				return { ...inspectionType };
			}
		});
		this.inspectionTypes$.next(this.inspectionTypes.filter(ins => ins.selected));
		this.rulesService.ruleToPatch$.pipe(filter(isDefined), first()).subscribe(rule => {
			this.setInspTypesForPatchedRuleFwd(this.inspectionTypes, rule);
		});
		this.update();
	}
	get noInspectionTypeSelected(): boolean {
		return !this.inspectionTypes.some(type => type.selected);
	}

	setZonePlaceholderText(zones: DisplayedZone[]) {
		if (zones?.length > 1) {
			this.zonesSelectionLabel = "Multiple Zones Selected";
			this.hasZonesSelected = true;
		} else if (zones?.length === 1) {
			this.zonesSelectionLabel = zones[0].name;
			this.hasZonesSelected = true;
		} else {
			this.zonesSelectionLabel = "Select Zones";
			this.hasZonesSelected = false;
		}
	}

	private cloneDisplayedZones(zones: DisplayedZone[] | null | undefined): DisplayedZone[] {
		return zones ? zones.map(zone => ({ ...zone })) : [];
	}

	closeCategoryZoneDialog() {
		this.zonesService.closeDialog$.pipe(takeUntil(this.destroy$)).subscribe(_ => {
			this.dialog.closeAll();
		});
	}

	openCategoryZoneDialog() {
		const dialogRef = this.dialog.open(ZonesSelectorComponent, {
			width: "1000px",
			height: "100vh",
			data: {
				tripType: this.semantics,
				rule: this.rulesService.ruleToPatch$.value,
			},
			disableClose: true,
			autoFocus: true,
			restoreFocus: true,
		});

		// Restore focus to the zone selector button after the dialog closes
		dialogRef.afterClosed().subscribe(() => {
			const root = this.ref.nativeElement as HTMLElement;
			const zoneBtn = root.querySelector<HTMLElement>(".zone-selector");
			if (zoneBtn) {
				zoneBtn.focus();
			}
		});
	}

	onRadioBtnChange(event) {
		if (event === this.radioButtonValues.ZONE) {
			this.disableZoneDropdown = false;
		} else {
			this.disableZoneDropdown = true;
			this.selectedZones$.next([]);
		}
	}

	/** Toggle a day checkbox for a work period via keyboard. */
	toggleDayCheckbox(periodIndex: number, dayIndex: number): void {
		const dayKey = this.dayFormKeys[dayIndex];
		const daysGroup = (this.workPeriods.at(periodIndex) as FormGroup).get("days") as FormGroup;
		const ctrl = daysGroup.get(dayKey);
		if (ctrl) {
			ctrl.setValue(!ctrl.value);
		}
	}

	isNotifierShowed(index: number) {
		const { allWeek, days, start, end } = this.form?.get("workPeriods")?.value[index];

		if (allWeek || Object.values(days).filter(Boolean).length === 0 || start === "00:00 AM" || end === "00:00 AM") {
			return false;
		}

		const startTime = new Date(`2025-01-01T${this.convertTo24Hour(start)}`).getTime();
		const endTime = new Date(`2025-01-01T${this.convertTo24Hour(end)}`).getTime();
		return endTime - startTime < 0;
	}

	private convertTo24Hour(time: string) {
		const [timePart, modifier] = time.split(" ");
		let [hours, minutes] = timePart.split(":").map(Number);

		if (modifier.toLowerCase() === "pm" && hours !== 12) hours += 12;
		if (modifier.toLowerCase() === "am" && hours === 12) hours = 0;

		return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:00`;
	}
}
